import type { ReactNode } from 'react';
import { useParams } from 'react-router';
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderEditableName,
  PageHeaderIdentity,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { notificationService } from '@/lib/services/notification.service';
import { useUpdateChatSession } from '../../model/useUpdateChatSession';
import { useTranslation } from 'react-i18next';

export function RedesignedChatHeader({
  chatTitle,
  onRename,
  actions,
}: {
  chatTitle?: string;
  /** Keeps the header's own title in step: it is local state, not query cache. */
  onRename: (title: string) => void;
  actions?: ReactNode;
}) {
  const { t } = useTranslation('chats');
  const { chatId } = useParams<{ chatId: string }>();
  const { mutateAsync: updateSession } = useUpdateChatSession();

  const title = chatTitle ?? t('sessions.redesignedChatHeader.newChat');

  /**
   * The name is committed locally before the request so typing stays responsive,
   * which means a rejection has to put the old one back — otherwise the header
   * keeps showing a title the server never accepted, and the next poll silently
   * reverts it with nothing on screen to explain why.
   */
  const renameSession = async (next: string) => {
    if (!chatId) return;

    const previous = title;
    onRename(next);

    try {
      await updateSession({ contextId: chatId, title: next });
    } catch {
      onRename(previous);
      notificationService.showError(t('sessions.sessionsTitleCell.failedToSaveTitle'), {
        persist: false,
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <PageHeader className="shrink-0">
      <PageHeaderIdentity>
        <PageHeaderBreadcrumb>
          <PageHeaderBreadcrumbLink href={ROUTES.SESSIONS}>
            {t('sessions.sessions')}
          </PageHeaderBreadcrumbLink>
          {/* A session with no id yet cannot be renamed, so it reads as a plain crumb. */}
          {chatId ? (
            <PageHeaderEditableName
              value={title}
              onCommit={(next) => void renameSession(next)}
              label={t('sessions.redesignedChatHeader.sessionName')}
            />
          ) : (
            <PageHeaderBreadcrumbPage>{title}</PageHeaderBreadcrumbPage>
          )}
        </PageHeaderBreadcrumb>
      </PageHeaderIdentity>
      {actions && <PageHeaderActions>{actions}</PageHeaderActions>}
    </PageHeader>
  );
}
